import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { API_PATH } from 'src/environments/environment';
import { ContractListEntityType } from 'src/app/domain/registration/interfaces/contract-list.interfaces';

@Injectable({
    providedIn: 'root'
})
export class ContractGenerationService {
    private readonly apiUrl = `${API_PATH}contracts/`;

    constructor(private httpClient: HttpClient) { }

    generateContract(contractData: any): Observable<any> {
        if (contractData.signatures && Array.isArray(contractData.signatures)) {
            contractData.signatures = contractData.signatures.map((sig: any) => {
                if (sig.type && !sig.role) {
                    sig.role = sig.type;
                }

                if (sig.isDefaultContracted) {
                    return { ...sig, customerId: "0" };
                }

                if (sig.isContracted) {
                    return { ...sig, customerId: contractData.customerId };
                }

                return sig;
            });
        }

        return this.httpClient.post(`${this.apiUrl}`, contractData).pipe(
            catchError(error => throwError(() => error))
        );
    }

    generateStandaloneContract(contractData: any): Observable<any> {
        if (contractData instanceof FormData) {
            return this.httpClient.post(`${this.apiUrl}standalone`, contractData).pipe(
                catchError(error => throwError(() => error))
            );
        } else {
            return this.httpClient.post(`${this.apiUrl}standalone`, contractData, {
                headers: { 'Content-Type': 'application/json' }
            }).pipe(
                catchError(error => throwError(() => error))
            );
        }
    }

    previewContract(contractData: any): Observable<Blob> {
        return this.httpClient.post(`${this.apiUrl}preview`, contractData, {
            responseType: 'blob'
        }).pipe(
            catchError(error => {
                return throwError(() => new Error('Falha ao gerar prévia do contrato.'));
            })
        );
    }

    previewPartnerContract(data: { partnerId: string; contractType: string; systemId?: string }): Observable<Blob> {
        return this.httpClient.post(`${this.apiUrl}partner/preview`, data, {
            responseType: 'blob'
        }).pipe(
            catchError(error => {
                return throwError(() => new Error('Falha ao gerar prévia do contrato de parceiro.'));
            })
        );
    }

    previewWithTemporaryTemplate(contractData: any, templateContent: string): Observable<Blob> {
        const isPartner = contractData.partnerId && (!contractData.customerId || contractData.customerId.startsWith('partner-preview-'));

        const requestData = {
            ...contractData,
            templateContent
        };

        return this.httpClient.post(`${this.apiUrl}preview-custom`, requestData, {
            responseType: 'blob'
        }).pipe(
            catchError(error => {
                if (error.status === 400 && error.error && isPartner) {
                    const fallbackRequest = {
                        ...requestData,
                        customerId: 'fallback-customer'
                    };

                    return this.httpClient.post(`${this.apiUrl}preview-custom`, fallbackRequest, {
                        responseType: 'blob'
                    }).pipe(
                        catchError(() => {
                            return throwError(() => new Error('Falha ao gerar prévia do contrato.'));
                        })
                    );
                }

                return throwError(() => new Error('Falha ao gerar prévia do contrato.'));
            })
        );
    }

    getTemplateHtmlContent(templateId: string): Observable<string> {
        return this.httpClient.get(`${this.apiUrl}template/${templateId}/html-content`, {
            responseType: 'text'
        }).pipe(
            catchError(() => {
                return throwError(() => new Error('Falha ao obter conteúdo HTML do template.'));
            })
        );
    }

    previewWithEditedHtml(contractData: any, htmlContent: string): Observable<Blob> {
        const isPartner = contractData.entityType === 'partner' ||
            (contractData.partnerId && (!contractData.customerId || contractData.customerId.startsWith('partner-preview-')));

        const requestData = {
            ...contractData,
            htmlContent,
            customerId: isPartner
                ? (contractData.customerId || 'fallback-customer')
                : contractData.customerId
        };

        return this.httpClient.post(`${this.apiUrl}preview-with-edited-html`, requestData, {
            responseType: 'blob'
        }).pipe(
            catchError(error => {
                if (error.status === 400 && error.error && isPartner) {
                    return this.previewWithTemporaryTemplate(requestData, htmlContent);
                }

                return throwError(() => new Error('Falha ao gerar prévia do contrato.'));
            })
        );
    }

    generateContractWithEditedHtml(contractData: any, htmlContent: string): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}generate-with-edited-html`, {
            ...contractData,
            htmlContent
        }).pipe(
            catchError(error => throwError(() => error))
        );
    }

    getDefaultContractedCustomer(): Observable<any> {
        return this.httpClient.get<any>(`${API_PATH}customers/default-contracted`);
    }

    getTemplateContent(templateId: string): Observable<string> {
        return this.httpClient.get(`${this.apiUrl}template/${templateId}/content`, {
            responseType: 'text'
        });
    }

    updateTemplateContent(templateId: string, content: string): Observable<any> {
        return this.httpClient.put(`${this.apiUrl}template/${templateId}/content`, { content });
    }

    getAllTemplates(): Observable<any[]> {
        return this.httpClient.get<any[]>(`${this.apiUrl}template`);
    }

    getPartnerContractTemplates(): Observable<Record<string, any>> {
        return this.httpClient.get<Record<string, any>>(`${this.apiUrl}templates/by-type`);
    }

    uploadHtmlTemplate(formData: FormData): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}template/html`, formData);
    }

    uploadDocxTemplate(formData: FormData): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}template/docx`, formData);
    }

    savePartnerContractTemplate(formData: FormData): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}partner-templates`, formData);
    }

    getById(contractId: string): Observable<any> {
        return this.httpClient.get(`${this.apiUrl}${contractId}`);
    }

    updateContractStatus(contractId: string): Observable<any> {
        return this.httpClient.put(`${this.apiUrl}${contractId}/update-status`, {});
    }

    forceRefreshStatus(contractId: string): Observable<any> {
        return this.httpClient.put(`${this.apiUrl}${contractId}/force-refresh`, {});
    }

    getContractPreview(contractId: string): Observable<Blob> {
        return this.httpClient.get(`${this.apiUrl}${contractId}/preview`, {
            responseType: 'blob'
        });
    }

    downloadContract(contractId: string): Observable<Blob> {
        return this.httpClient.get(`${this.apiUrl}${contractId}/download`, {
            responseType: 'blob'
        });
    }

    getZapSignViewUrl(contractId: string): Observable<{ url: string }> {
        return this.httpClient.get<{ url: string }>(`${this.apiUrl}${contractId}/zapsign-view-url`);
    }

    getContractFile(contractId: string): Observable<Blob> {
        return this.httpClient.get(`${this.apiUrl}${contractId}/file`, {
            responseType: 'blob'
        });
    }

    getSignatureTypeLabel(type: string): string {
        const labels: Record<string, string> = {
            'CONTRATADA': 'Contratada',
            'CONTRATANTE': 'Contratante',
            'TESTEMUNHA': 'Testemunha',
            'FIADOR': 'Fiador',
            'PARCEIRO': 'Parceiro',
            'UNIDADE': 'Unidade'
        };
        return labels[type] || type;
    }

    getTemplateStandalone(): Observable<any[]> {
        return this.httpClient.get<any[]>(`${this.apiUrl}templates/manual`);
    }


}

